import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PageBody, Toast, WeekNav, useNow } from '@wundo/ui';
import type { MeetingBucket } from '@wundo/shared/calendar.ts';
import { PageTitle } from '@/components/PageTitle.tsx';
import { AgendaToolbar, type AgendaView } from '@/components/agenda/AgendaToolbar.tsx';
import { AddMeetingButton } from '@/components/agenda/AddMeetingButton.tsx';
import { AddMeetingDialog } from '@/components/agenda/AddMeetingDialog.tsx';
import { AgendaListView } from '@/components/agenda/AgendaListView.tsx';
import { AgendaWeekView } from '@/components/agenda/AgendaWeekView.tsx';
import { CalendarSourcePill } from '@/components/agenda/CalendarSourcePill.tsx';
import { NoCalendarEmptyState } from '@/components/NoCalendarEmptyState.tsx';
import { passesFilter, toggleBucket } from '@/components/agenda/agendaFilter.ts';
import { formatWeekRange } from '@/components/agenda/meetingFormat.ts';
import { useAddToWundo } from '@/lib/addToWundo.ts';
import { storeAgendaBuckets, storedAgendaBuckets } from '@/lib/agendaBuckets.ts';
import { useProfileSave } from '@/lib/profileSave.ts';
import { useLinkJourneySuggestion } from '@/lib/linkJourneySuggestion.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import {
  fetchHasAnyMeeting,
  fetchMeetings,
  type CalendarMeetingItem,
} from '@/lib/meetings.ts';
import { fetchConnectedProviders } from '@/lib/integrations.ts';
import { ensurePilotContext } from '@wundo/client/pilot.ts';
import { currentUserOrNull } from '@/lib/pilotEnded.ts';
import { usePublishChatPageOffer } from '@/lib/chatPageOffer.ts';
import { useChatPageNudge } from '@/lib/useChatPageNudge.ts';
import { addDays, daysFrom, startOfWeek } from '@/lib/week.ts';
import { m } from '@/paraglide/messages.js';

/* The agenda is the app's landing page until there is a home page to land on. */
export const Route = createFileRoute('/_app/')({
  /*
   * Except for a pilot member, whose landing page is the oefenbibliotheek. The
   * pilot is about the practice conversations, and an agenda is the one page
   * that says nothing at all to somebody who is here for those — on the first
   * visit after onboarding and every time they open the app after that.
   */
  beforeLoad: async ({ cause, context, search }) => {
    /*
     * Where somebody lands is decided when they arrive. Opening the agenda from
     * inside the app, or tidying the mail's request out of the address, is not
     * an arrival: the agenda is still where a pilot member plans a conversation.
     */
    if (cause !== 'enter' || !context.arriving) {
      return;
    }

    /* A demo organisation is there to practise, so its members land there too. */
    const user = await currentUserOrNull(context.queryClient);
    if (user?.demo) {
      throw redirect({ to: '/practice', replace: true });
    }

    /* Unless the weekly mail asked for this page's form, which is only here. */
    if (search.plan) {
      return;
    }

    const pilot = await ensurePilotContext(context.queryClient);

    if (pilot !== null) {
      throw redirect({ to: '/practice', replace: true });
    }
  },
  component: AgendaRoute,
  /*
   * `?plan=1` is the weekly mail's "Gesprek inplannen": the form for writing a
   * conversation down is a dialog on this page, not a page of its own. The
   * router rewrites the address from what comes out of here and reads it back,
   * so `true` has to be recognised as well, or the request drops out on load.
   */
  validateSearch: (search: Record<string, unknown>): AgendaSearch => ({
    plan:
      search.plan === true || search.plan === 1 || search.plan === '1' ? true : undefined,
  }),
});

interface AgendaSearch {
  /** Whether to open the add-conversation form as the page comes up. */
  plan?: boolean;
}

/* Reading the search is the route's job, so the page itself stays renderable. */
function AgendaRoute() {
  const { plan } = Route.useSearch();

  return <AgendaPage planOnMount={plan === true} />;
}

export interface AgendaPageProps {
  /** Open the add-conversation form on mount, for a reader who asked for it. */
  planOnMount?: boolean;
}

/* Until the preferences load. Both are personal settings on the agenda tab. */
const DEFAULT_WEEK_STARTS_ON = 'monday';

/* Where the drawer beside this page is standing, for the nudge it asks for. */
const NUDGE_PAGE = { page: 'agenda' } as const;

export function AgendaPage({ planOnMount = false }: AgendaPageProps) {
  /* The one thing worth recommending here to somebody new (DEV-72). */
  const nudge = useChatPageNudge(NUDGE_PAGE);

  /* This page's conversation starters (DEV-16). */
  usePublishChatPageOffer({
    quickActions: [
      { text: m.chat_quick_agenda_unprepared(), icon: 'prepare' },
      { text: m.chat_quick_agenda_attention(), icon: 'week' },
    ],
    nudge,
  });

  const navigate = useNavigate();

  /*
   * Which week, counted from this one, rather than a fixed date. The week start
   * is a preference that arrives after the first render, and an absolute date
   * pinned at mount would keep pointing at the Monday of a week the user has
   * since asked to start on Sunday.
   */
  const [weekOffset, setWeekOffset] = useState(0);
  /* Which drawers of the calendar are showing, as the user last left them. */
  const [buckets, setBuckets] = useState<readonly MeetingBucket[]>(storedAgendaBuckets);
  /*
   * null means "whatever my default is". Flipping it in the toolbar saves that
   * default too, so the toolbar and the settings page never disagree; the local
   * value only spares the flip a wait on the server.
   */
  const [chosenView, setChosenView] = useState<AgendaView | null>(null);
  const { save: saveProfile } = useProfileSave();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  /*
   * Whether there is a calendar at all. With one, the week is the calendar's
   * and Wundo does not put anything in it; without one, writing a meeting down
   * is the only way this page ever has anything on it.
   */
  const { data: connected } = useQuery({
    queryKey: ['integrations'],
    queryFn: fetchConnectedProviders,
  });
  const noCalendar = connected !== undefined && !connected.microsoft && !connected.google;

  const addToWundo = useAddToWundo(user?.id);
  const linkSuggestion = useLinkJourneySuggestion(user?.id);
  /* One toast on the page: the last action speaks. */
  const toast = linkSuggestion.message !== null ? linkSuggestion : addToWundo;

  /*
   * The mail's "Gesprek inplannen" asks for the form, which is a dialog on this
   * page. Somebody with a calendar is never offered it, so for them the request
   * says nothing; the address drops it once the form is up, so a refresh does
   * not put the dialog back in front of a page that was being read.
   */
  const [planAsked, setPlanAsked] = useState(planOnMount);
  const planDialogOpen = planAsked && noCalendar && user !== undefined;

  useEffect(() => {
    if (planDialogOpen) {
      void navigate({ to: '/', search: {}, replace: true });
    }
  }, [planDialogOpen, navigate]);

  const view = chosenView ?? user?.agenda_default_view ?? 'week';
  const weekStartsOn = user?.agenda_week_starts_on ?? DEFAULT_WEEK_STARTS_ON;
  /* The weekend switch is the day count: five weekdays, or the whole week. */
  const visibleDays = user?.agenda_show_weekend ? 7 : 5;

  const now = useNow();
  const weekStart = useMemo(
    () => startOfWeek(addDays(now, weekOffset * 7), weekStartsOn),
    [now, weekOffset, weekStartsOn],
  );

  const days = useMemo(() => daysFrom(weekStart, visibleDays), [weekStart, visibleDays]);

  const {
    data: meetings = [],
    isPending,
    isError,
  } = useQuery({
    queryKey: ['meetings', user?.id, weekStart.toISOString(), visibleDays],
    queryFn: () => fetchMeetings(user!.id, weekStart, addDays(weekStart, visibleDays)),
    enabled: Boolean(user?.id),
  });

  const visible = meetings.filter((meeting) => passesFilter(meeting, buckets));

  /* A week that failed to load is not an empty one, and asks nothing further. */
  const emptyWeek = noCalendar && !isError && meetings.length === 0;

  /*
   * Whether this agenda has ever held anything. The week's own fetch cannot
   * say: it knows about the days on screen only, and somebody paging through a
   * quiet week may have a hundred meetings in the weeks either side of it. So
   * an empty week asks once, and the answer is kept for all the other weeks.
   */
  const { data: hasAnyMeeting, isError: probeFailed } = useQuery({
    /* Under 'meetings', so writing a meeting down settles this question again. */
    queryKey: ['meetings', user?.id, 'any'],
    queryFn: () => fetchHasAnyMeeting(user!.id),
    enabled: Boolean(user?.id) && !isPending && emptyWeek,
  });
  const probing = emptyWeek && hasAnyMeeting === undefined && !probeFailed;

  /*
   * An agenda with nothing on it at all, for somebody whose calendar Wundo
   * cannot see. The per-day placeholders answer "nothing on Tuesday", which is
   * true and useless here — the page is empty because Wundo has been told
   * nothing, not because the week is quiet. A connected calendar keeps the
   * ordinary empty week, because there an empty week is simply a quiet one.
   */
  const nothingAtAll = emptyWeek && hasAnyMeeting === false;

  /* One toast on the page, so linking takes down the adding one. */
  const linkToSuggestedStep = (meeting: CalendarMeetingItem) => {
    addToWundo.dismiss();
    linkSuggestion.link(meeting);
  };

  /*
   * The one click. A conversation Wundo is in opens; one it is not in joins —
   * on the journey step Wundo suggested for it, if any — and the user stays on
   * their week, so several can be added in one sweep.
   */
  const select = (meeting: CalendarMeetingItem) => {
    if (meeting.tracked) {
      void navigate({ to: '/meetings/$meetingId', params: { meetingId: meeting.id } });
      return;
    }
    if (meeting.journey_suggestion) {
      linkToSuggestedStep(meeting);
      return;
    }
    linkSuggestion.dismiss();
    addToWundo.add(meeting);
  };

  return (
    <>
      <PageTitle>{m.nav_agenda()}</PageTitle>
      <PageBody>
        <AgendaToolbar
          view={view}
          onViewChange={(next) => {
            setChosenView(next);
            saveProfile({ agenda_default_view: next });
          }}
          buckets={buckets}
          onFilterSelect={(value) => {
            const next = toggleBucket(buckets, value);
            setBuckets(next);
            storeAgendaBuckets(next);
          }}
          onToday={() => setWeekOffset(0)}
          action={
            /*
             * One "Gesprek toevoegen" on the page. While the empty state is up
             * it carries the button, because that is where somebody is looking.
             */
            noCalendar && user && !nothingAtAll && !probing ? (
              <AddMeetingButton userId={user.id} />
            ) : null
          }
          source={
            connected && (
              <CalendarSourcePill
                microsoft={connected.microsoft}
                google={connected.google}
              />
            )
          }
        />

        <div className='mt-6'>
          {/*
            The probe stays behind the same loading line as the week itself:
            either answer would otherwise put up a page that has to be taken
            down again a moment later.
          */}
          {(isPending || probing) && (
            <p className='text-base text-ink-muted'>{m.agenda_loading()}</p>
          )}
          {isError && <p className='text-base text-danger'>{m.agenda_error()}</p>}
          {!isPending && !probing && !isError && (
            <>
              {/*
                The week arrows belong to the grid. The list names each day as
                it goes, so a bar over it saying which week this is repeats what
                the first day header already says.
              */}
              {view === 'week' && (
                <WeekNav
                  label={formatWeekRange(weekStart, addDays(weekStart, visibleDays - 1))}
                  previousLabel={m.agenda_previous_week()}
                  nextLabel={m.agenda_next_week()}
                  onPrevious={() => setWeekOffset((weeks) => weeks - 1)}
                  onNext={() => setWeekOffset((weeks) => weeks + 1)}
                />
              )}
              {nothingAtAll && user ? (
                <NoCalendarEmptyState
                  className='mt-4'
                  userId={user.id}
                  title={m.agenda_empty_title()}
                  description={m.agenda_empty_body()}
                />
              ) : view === 'week' ? (
                <AgendaWeekView
                  days={days}
                  meetings={visible}
                  onSelect={select}
                  onLink={linkToSuggestedStep}
                />
              ) : (
                <AgendaListView
                  days={days}
                  meetings={visible}
                  onSelect={select}
                  onLink={linkToSuggestedStep}
                />
              )}
            </>
          )}
        </div>
      </PageBody>

      {/*
        The same form the button opens, opened by the mail instead — the page
        holds it because the button is not always the one on screen.
      */}
      {planAsked && user && (
        <AddMeetingDialog
          userId={user.id}
          open={planDialogOpen}
          onClose={() => setPlanAsked(false)}
        />
      )}

      {/*
        Outside PageBody: the toast belongs to the window, and it is what makes
        one click safe without a question in front of it.
      */}
      <Toast
        open={toast.message !== null}
        message={toast.message ?? ''}
        actionLabel={toast.undoable ? m.agenda_undo() : undefined}
        onAction={toast.undoable ? toast.undo : undefined}
      />
    </>
  );
}
